// Modules
import type {GetStaticProps} from 'next';
import {useTranslation} from '@app/hooks/use-translation';
import {serverSideTranslations} from '@app/modules/locale-management/server';
import {LandingSections} from '@tempo/landing';
import {TempoLandingPage} from '@app/components/layout/LandingPage';
import type {LandingProps} from '@app/types/next';
import {
	FAQ,
	OfflineDevices,
	OfflineHero,
	OfflineSave,
	OfflineTap,
} from '@app/views/features/offline';
import {PlaylistSection} from '@app/views/generic';
import {LandingButtonLink} from '@app/components/landing-btn-link';
import {useLocation} from '@app/hooks/use-location';
import {useOfflinePageData} from '@app/modules/ssg-data/features/offline/client';
import {useAsRailItems} from '@app/modules/ssg-data/utils';
import {getOfflinePageGQLData} from '@app/modules/ssg-data/features/offline/server';
import {getFooterLinksPerLang} from '@app/modules/ssg-data/unlogged-data';

const OfflinePage: React.FC<LandingProps> = () => {
	const {t} = useTranslation();
	const pageTitle = t('offlinelandingpage_text_seotitle_standalone-web', {
		ns: 'offline',
	});
	const pageDescription = t(
		'offlinelandingpage_text_seodescription_standalone-web',
		{ns: 'offline'},
	);
	const {getDeezerUrl} = useLocation();
	const {playlistAsRailItem} = useAsRailItems();
	const {data} = useOfflinePageData();
	return (
		<TempoLandingPage title={pageTitle} description={pageDescription}>
			<LandingSections topGutter>
				<OfflineHero />
				<OfflineSave />
				<OfflineTap />
				<OfflineDevices />
				<PlaylistSection
					title={t(
						'offlinelandingpage_title_playlistsforonthego_standalone-web',
						{ns: 'offline'},
					)}
					description={t(
						'offlinelandingpage_text_playlistsforonthego_standalone-web',
						{ns: 'offline'},
					)}
					playlistItems={playlistAsRailItem(data?.playlists.edges)}
					cta={
						<LandingButtonLink
							tracking={{
								eventaction: 'discover',
								eventlabel: 'content_discovery',
							}}
							href={getDeezerUrl('/channels/explore')}
						>
							{t('footer_title_explorechannels_standalone-features', {
								ns: 'common',
							})}
						</LandingButtonLink>
					}
				/>
				<FAQ />
			</LandingSections>
		</TempoLandingPage>
	);
};

export const getStaticProps: GetStaticProps<LandingProps> = async ({
	locale = '',
	locales = [],
}) => {
	const translations = await serverSideTranslations(locale, [
		'offline',
		'features',
		'common',
	]);
	return {
		props: {
			...translations,
			dehydratedState: {
				...(await getFooterLinksPerLang({locale, locales})),
				...(await getOfflinePageGQLData({locale})),
			},
			tracking: {
				pagecategory: 'features',
				pagename: 'offline',
				cdpViewId: 'features_offline',
			},
		},
		revalidate: 15 * 60,
	};
};

export default OfflinePage;
